import React, { useEffect, useRef, useState } from 'react';
import {
	StatusCircle,
	AreaQuiet,
	AssigneeChip,
	DueLabel,
	GateBadge,
	SourceIcon,
	MailChip,
	PriorityMark,
	SubtaskProgress,
	TriageBadge,
	accentStyle,
} from './TaskBits';
import { applyShowCompleted } from './taskModel';
import DueQuickSet from './DueQuickSet';
import TaskQuickBar from './TaskQuickBar';
import { useIsMobile } from './useIsMobile';

// Portal Work — the task row (core list artifact), v5 redesign.
// The row is one line in a continuous, hairline-divided list: the area is the
// 4px left bar (never a repeated pill), the status circle sits in a 44px hit
// target, and clicking the row opens the detail. There is deliberately NO
// pencil/kebab on the row — mark-complete lives on the status circle, everything
// else lives in the detail. On mobile the row collapses to
// [status · title/meta · due-or-gate + avatar]; at >=768px it opens into aligned
// columns [status · title · area · assignee · project · due · source]. A parent
// with sub-tasks shows an expand chevron (hover-revealed on desktop).
//
// TAP BEHAVIOUR FORKS AT 768px, BY ANTHONY'S DECISION (2026-08-13):
//   • PHONE — a tap EXPANDS the row into its quick-action bar (date, assignee,
//     priority, area, park) rather than opening the detail; the bar carries an
//     explicit Open button for that. The whole point is not having to open a
//     task to set a date on it, and on a phone the desktop due-cell control is
//     too small a target to be the answer.
//   • DESKTOP — completely unchanged. A click still opens the detail, because
//     that gesture is muscle memory on the surface Anthony works in every day,
//     and the due cell there is already a direct control (DueQuickSet).
// The fork is behavioural, not cosmetic, so it needs the breakpoint in JS.
function TaskRow({
	task,
	children = [],
	projectsById = {},
	now,
	onOpen,
	onAdvance,
	onAdvanceChild,
	onSetDeadline,
	onQuickUpdate,
	fields,
	areaOrder,
	offeredGates,
	hideAreaCol = false,
	change = null,
	showCompleted = false,
}) {
	const [expanded, setExpanded] = useState(false);
	const [barOpen, setBarOpen] = useState(false);
	const isMobile = useIsMobile();
	const rowRef = useRef(null);
	// The bar is only ever reachable where a handler was wired AND we are on a
	// phone — ProjectDetail and the tests mount rows without it and keep the old
	// behaviour exactly.
	const barEnabled = isMobile && !!onQuickUpdate;
	const hasChildren = children.length > 0;
	// RENDER the children the viewer asked to see; COUNT all of them. The
	// progress pill below still reads "2 of 5" when three completed children are
	// hidden — hiding finished work should not make a parent look like it has
	// less work than it does.
	const shownChildren = applyShowCompleted(children, showCompleted);
	const hiddenChildCount = children.length - shownChildren.length;
	const parked = !!task.gate;
	const project = task.projectId ? projectsById[task.projectId] : null;
	// A gate outranks the deadline (taskModel → dueBucket), so a parked row keeps
	// its badge and gets no date setter. Without a handler this falls back to the
	// plain label, so every existing mount (ProjectDetail, tests) is unchanged.
	// On a PHONE the end-cluster date stays a plain read-only label even though
	// the desktop cell is a control: the whole row is the tap target there, and a
	// second interactive date on it would swallow the tap that opens the bar —
	// two different date UIs on one row, chosen by where your thumb landed.
	const dueCell = (compact) => {
		const interactive = onSetDeadline && !(compact && barEnabled);
		return parked ? (
			<GateBadge gate={task.gate} />
		) : interactive ? (
			<DueQuickSet
				task={task}
				now={now}
				onSetDeadline={onSetDeadline}
				compact={compact}
			/>
		) : (
			<DueLabel task={task} now={now} />
		);
	};

	// Tapping a DIFFERENT row (or anywhere outside this one) collapses this bar,
	// so the list never accumulates a column of open bars. Kept local rather than
	// lifted into TaskListView: the listener is only attached while this row's
	// bar is actually open, so there is at most one in the document.
	useEffect(() => {
		if (!barOpen) return undefined;
		const onDocDown = (e) => {
			if (rowRef.current?.contains(e.target)) return;
			setBarOpen(false);
		};
		document.addEventListener('pointerdown', onDocDown);
		return () => document.removeEventListener('pointerdown', onDocDown);
	}, [barOpen]);

	const activate = () => {
		if (barEnabled) setBarOpen((v) => !v);
		else onOpen?.(task);
	};

	return (
		<div className="wt-rowwrap" style={accentStyle(task.area)} ref={rowRef}>
			<div
				className={`wt-row${task.status === 'completed' ? ' is-done' : ''}${
					barOpen ? ' is-expanded' : ''
				}`}
				role="button"
				tabIndex={0}
				aria-expanded={barEnabled ? barOpen : undefined}
				onClick={activate}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						activate();
					}
				}}>
				<StatusCircle
					status={task.status}
					onClick={() => onAdvance?.(task)}
				/>

				<div className="wt-row__main">
					<div className="wt-row__titleline">
						{hasChildren && (
							<button
								type="button"
								className={`wt-row__chev${expanded ? ' is-open' : ''}`}
								aria-label={expanded ? 'Collapse' : 'Expand'}
								onClick={(e) => {
									e.stopPropagation();
									setExpanded((v) => !v);
								}}>
								<i
									className={`fa-sharp fa-solid ${
										expanded ? 'fa-chevron-up' : 'fa-chevron-down'
									}`}
								/>
							</button>
						)}
						<span className="wt-row__title">{task.title}</span>
						{change?.changed && (
							<span
								className="wt-row__changed"
								title={
									change.newComments > 0
										? `${change.newComments} new comment${
												change.newComments === 1 ? '' : 's'
										  } since you last looked`
										: 'Changed since you last looked'
								}>
								<span className="wt-row__changed-dot" aria-hidden="true" />
								{change.newComments > 0 && (
									<span className="wt-row__changed-count">
										{change.newComments}
									</span>
								)}
								<span className="sr-only">
									{change.newComments > 0
										? `${change.newComments} new comments since you last looked`
										: 'Changed since you last looked'}
								</span>
							</span>
						)}
						{/* Mail identity on the title line at EVERY width — the source
						    column below is desktop-only and its glyph is quiet. */}
						<MailChip task={task} />
						<PriorityMark priority={task.priority} />
					</div>
					<div className="wt-row__submeta">
						{hasChildren && <SubtaskProgress children={children} />}
						<TriageBadge task={task} />
					</div>
				</div>

				{/* desktop columns */}
				<div className="wt-row__cols">
					{!hideAreaCol && (
						<div className="wt-cell wt-cell--area">
							<AreaQuiet area={task.area} />
						</div>
					)}
					<div className="wt-cell wt-cell--assignee">
						<AssigneeChip task={task} size={20} />
					</div>
					<div className="wt-cell wt-cell--project">
						{project && (
							<span className="wt-ref" title={project.title}>
								<i className="fa-sharp-duotone fa-solid fa-folder" />
								{project.title}
							</span>
						)}
					</div>
					<div className="wt-cell wt-cell--due">{dueCell(false)}</div>
					<div className="wt-cell wt-cell--source">
						<SourceIcon source={task.source} />
					</div>
				</div>

				{/* mobile end cluster */}
				<div className="wt-row__end">
					{dueCell(true)}
					<AssigneeChip task={task} initialOnly size={20} />
				</div>
			</div>

			{barOpen && barEnabled && (
				<TaskQuickBar
					task={task}
					now={now}
					fields={fields}
					areaOrder={areaOrder}
					offeredGates={offeredGates}
					onQuickUpdate={onQuickUpdate}
					onOpen={onOpen}
				/>
			)}

			{hasChildren && expanded && (
				<div className="wt-children">
					{shownChildren.map((child) => (
						<div
							key={child.id}
							className={`wt-child${
								child.status === 'completed' ? ' is-done' : ''
							}`}
							role="button"
							tabIndex={0}
							onClick={() => onOpen?.(child)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') onOpen?.(child);
							}}>
							<StatusCircle
								status={child.status}
								size="sm"
								onClick={() => onAdvanceChild?.(child)}
							/>
							<span className="wt-child__title">{child.title}</span>
							{onSetDeadline ? (
								<DueQuickSet
									task={child}
									now={now}
									onSetDeadline={onSetDeadline}
									compact
								/>
							) : (
								<DueLabel task={child} now={now} />
							)}
						</div>
					))}
					{hiddenChildCount > 0 && (
						<p className="wt-children__hidden">
							{hiddenChildCount} completed{' '}
							{hiddenChildCount === 1 ? 'sub-task' : 'sub-tasks'} hidden
						</p>
					)}
				</div>
			)}
		</div>
	);
}

export default TaskRow;
